import { CopyButton } from "@/features/ordini/components/OrderCard";
import { labelFornitore } from "@/features/ordini/fornitoreFiltro";
import { formatDateIt, formatMoney } from "@/shared/format";
import { Pill, Riga, Righe, Sezione } from "./parti";
import type { ContextOrder, RiordinoFornitore } from "./tipi";

function Numero({ testo }: { testo: string }) {
  return (
    <>
      <span className="tabular-nums text-[12.5px]">{testo}</span>
      <CopyButton text={testo} label="Copia numero" />
    </>
  );
}

/** Lo stato dell'ordine al fornitore: il numero quando c'e', altrimenti cosa
 *  manca. Cosma e' FTP a senso unico: nessun numero torna, conta l'invio. */
function NumeroOrdine({ order }: { order: ContextOrder }) {
  if (order.supplier_order_ref) return <Numero testo={order.supplier_order_ref} />;
  if (order.supplier_kind === "cosma" && order.cosma_sent_at) {
    return <>inviato a Cosma il {formatDateIt(order.cosma_sent_at)}</>;
  }
  if (order.supplier_send_state === "to_send") return <Pill tono="giallo">Da ordinare</Pill>;
  if (order.supplier_send_state === "sent") return <>ordinato, numero non ancora arrivato</>;
  return <>non ancora arrivato</>;
}

function Riordino({ r, currency }: { r: RiordinoFornitore; currency: string }) {
  return (
    <Righe>
      <Riga label="Riordino">
        {r.rif || r.supplier_ref ? (
          <Numero testo={(r.rif || r.supplier_ref) as string} />
        ) : (
          "senza numero"
        )}
      </Riga>
      {r.rif && r.supplier_ref && (
        <Riga label="N° fornitore">
          <Numero testo={r.supplier_ref} />
        </Riga>
      )}
      {r.at && <Riga label="Chiesto il">{formatDateIt(r.at)}</Riga>}
      <Riga label="Merce pronta">
        {r.pronta_il ? formatDateIt(r.pronta_il) : <Pill tono="giallo">non ancora</Pill>}
      </Riga>
      <Riga label="Costo">
        {r.cost == null ? (
          <Pill tono="giallo">Importo non ancora arrivato</Pill>
        ) : (
          <>
            {formatMoney(r.cost, currency)} <span className="font-normal">netto</span>
          </>
        )}
      </Riga>
    </Righe>
  );
}

/** Chi ha la merce e a che punto e': il numero da citare al fornitore, la
 *  merce pronta, la LDV mandata, e i riordini (sostituzioni) uno per uno. */
export function Fornitore({
  order,
  riordini,
}: {
  order: ContextOrder;
  riordini: RiordinoFornitore[];
}) {
  const kind = order.supplier_kind && order.supplier_kind !== "unknown" ? order.supplier_kind : "";
  if (!kind && !order.supplier_order_ref && riordini.length === 0) return null;
  return (
    <Sezione titolo="Fornitore">
      <Righe>
        {kind && <Riga label="Fornitore">{labelFornitore(kind)}</Riga>}
        <Riga label="N° ordine">
          <NumeroOrdine order={order} />
        </Riga>
        {order.pickup_ready_at && (
          <Riga label="Merce pronta">{formatDateIt(order.pickup_ready_at)}</Riga>
        )}
        {order.pickup_mail_sent_at && (
          <Riga label="LDV inviata">{formatDateIt(order.pickup_mail_sent_at)}</Riga>
        )}
      </Righe>
      {riordini.map((r, i) => (
        <div
          key={`${r.rif ?? r.supplier_ref ?? ""}-${i}`}
          className="border-t border-dashed border-line-2 pt-2"
        >
          <Riordino r={r} currency={order.currency} />
        </div>
      ))}
    </Sezione>
  );
}
